import { Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useRequestPasswordReset } from '@/context/auth'
import { useToast } from '@/hooks/use-toast'
import { ApiError } from '@/lib/http'

const schema = z.object({ email: z.string().email('Enter a valid email') })
type FormValues = z.infer<typeof schema>

export function ForgotPasswordForm() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const mutation = useRequestPasswordReset()
  const [submitted, setSubmitted] = useState(false)
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { email: '' } })

  const onSubmit = handleSubmit(async ({ email }) => {
    try {
      await mutation.mutateAsync(email)
      setSubmitted(true)
      toast({
        title: 'Reset link sent',
        description:
          'If an account exists with that email, you will receive a password reset link.',
      })
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.errors?.email) setError('email', { message: err.errors.email[0] })
        toast({ title: 'Error', description: err.message, variant: 'destructive' })
      } else {
        toast({ title: 'Error', description: 'An unexpected error occurred', variant: 'destructive' })
      }
    }
  })

  if (submitted) {
    return (
      <div className="grid gap-6">
        <div className="text-center">
          <h3 className="text-lg font-medium">Check your email</h3>
          <p className="text-sm text-muted-foreground mt-2">
            If an account exists with that email, you will receive a password reset link.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate({ to: '/login' })}>
          Return to login
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <Card className="overflow-hidden">
        <CardContent className="grid p-0">
          <form className="p-6 md:p-8" onSubmit={onSubmit}>
            <div className="grid gap-6">
              <div className="flex flex-col items-center text-center">
                <h1 className="text-2xl font-semibold tracking-tight">Reset your password</h1>
                <p className="text-sm text-muted-foreground mt-2">
                  Enter your email and we&apos;ll send you a link to reset your password.
                </p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect="off"
                  disabled={isSubmitting}
                  {...register('email')}
                />
                {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
              </div>
              <Button type="submit" disabled={isSubmitting} className="mt-2">
                {isSubmitting ? 'Sending reset link...' : 'Send reset link'}
              </Button>
            </div>
            <div className="mt-4 text-center text-sm">
              Remember your password?{' '}
              <Link to="/login" className="underline underline-offset-4 hover:text-primary">
                Sign in
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
      <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary">
        By clicking continue, you agree to our <Link to="/terms">Terms of Service</Link> and{' '}
        <Link to="/privacy">Privacy Policy</Link>.
      </div>
    </div>
  )
}
